import { useEffect, useRef, useState } from 'react'

interface FontAnnouncementsOptions {
  isUsedCatalogLoading: boolean
  isFontCatalogError: boolean
  hasTextSelectionTarget: boolean
  fontFamilyIdMixed: boolean
  currentFontDisplayName: string | null
}

/** Text for the two visually hidden live regions: catalog loading, and font selection. */
export function useFontAnnouncements({
  isUsedCatalogLoading,
  isFontCatalogError,
  hasTextSelectionTarget,
  fontFamilyIdMixed,
  currentFontDisplayName,
}: FontAnnouncementsOptions) {
  const prevLoadingRef = useRef(false)
  const [fontLoadAnnouncement, setFontLoadAnnouncement] = useState('')

  useEffect(() => {
    if (isUsedCatalogLoading && !prevLoadingRef.current) {
      setFontLoadAnnouncement('Loading fonts...')
    } else if (!isUsedCatalogLoading && prevLoadingRef.current && !isFontCatalogError) {
      setFontLoadAnnouncement('Fonts ready.')
    }

    prevLoadingRef.current = isUsedCatalogLoading
  }, [isUsedCatalogLoading, isFontCatalogError])

  // Separate live region for font *selection* state (as opposed to catalog loading above) —
  // announces mixed-family selections and font family changes to screen reader users. Deduped
  // against the previous announcement so re-renders with an unchanged selection stay silent.
  const [fontSelectionAnnouncement, setFontSelectionAnnouncement] = useState('')
  const prevFontSelectionAnnouncementRef = useRef('')

  useEffect(() => {
    if (isUsedCatalogLoading || !hasTextSelectionTarget) return

    const next = fontFamilyIdMixed
      ? 'Multiple font families selected.'
      : currentFontDisplayName
        ? `Font: ${currentFontDisplayName}.`
        : ''

    if (next && next !== prevFontSelectionAnnouncementRef.current) {
      prevFontSelectionAnnouncementRef.current = next
      setFontSelectionAnnouncement(next)
    }
  }, [fontFamilyIdMixed, currentFontDisplayName, isUsedCatalogLoading, hasTextSelectionTarget])

  return { fontLoadAnnouncement, fontSelectionAnnouncement }
}
